import "./telemetry-entry";
import { PURPLE_THEME } from "@bestowinc/kindred-ds";
import {
  replaceProductAnalyticsMetadata,
  setProductAnalyticsEnabled,
} from "@bestowinc/logger/client";
import { SHELL_VERSION } from "@bestowinc/tinkertoy-shell-deps";
import { SHELL_CONFIG_ELEMENT_ID } from "@bestowinc/tinkertoy-core";
import {
  createModuleLoader,
  createViewLoader,
  mountShell,
  readSerializedShellConfig,
  warnIfDeveloperToolsDroppedDuringParse,
} from "@bestowinc/tinkertoy-client";

// Module/view bundles (and some transitive deps) read `process.env.NODE_ENV`.
// The browser has no `process`, so a bundle built without a `process` define
// throws `process is not defined` when it evaluates. Provide a minimal shim
// before any bundle is dynamically imported.
//
// NODE_ENV is "development", NOT "production": a correctly-built production
// bundle has NODE_ENV inlined at build time and never reads this shim, so the
// only readers are dev/un-defined bundles. Forcing "production" onto those
// makes a *development*-build React (e.g. an un-rebuilt CDN vendor) throw
// "running in production mode but dead code elimination has not been applied".
if (!Reflect.has(globalThis, "process")) {
  Reflect.set(globalThis, "process", { env: { NODE_ENV: "development" } });
}

const el = document.getElementById("root");
if (!el) {
  throw new Error('Root element "#root" not found');
}

// This same built shell is served verbatim by every production host (EC, CP,
// microsites, ...); each injects its own runtime config (API mount, CDN,
// carrier theme). The standalone demo injects nothing, so we fall back to the
// demo API/CDN and the Purple theme here.
const config = readSerializedShellConfig();
warnIfDeveloperToolsDroppedDuringParse(
  document.getElementById(SHELL_CONFIG_ELEMENT_ID)?.textContent,
  config,
);

// Boot seeds the host's baseline traits wholesale; later callers merge into it
// (e.g. the `applicationID` a module learns mid-enrollment).
setProductAnalyticsEnabled(config?.analytics?.enabled ?? false);
replaceProductAnalyticsMetadata(config?.analytics?.metadata ?? {});

// Expose the host's shared env vars as the global `ENV`, matching the
// `window.ENV` a Remix host injects, so bundled EC code that reads `ENV.*`
// resolves inside the non-Remix shell. Absent for the standalone demo, which
// leaves `ENV` unset.
if (config?.env) {
  Reflect.set(globalThis, "ENV", config.env);
}

const cdnBaseUrl = config?.cdnBaseUrl ?? "/cdn";

// Dev-only per-id bundle overrides (from the host's `ttv2-dev-override` cookie):
// flatten `{ id: { bundleUrl } }` → `{ id: bundleUrl }` for the loaders so the
// browser loads locally-served bundles instead of the CDN for overridden ids.
const flattenBundleUrls = (
  entries: Record<string, { bundleUrl: string }> | undefined,
): Record<string, string> | undefined =>
  entries === undefined
    ? undefined
    : Object.fromEntries(
        Object.entries(entries).map(([id, { bundleUrl }]) => [id, bundleUrl]),
      );

mountShell(el, {
  apiBaseUrl: config?.apiBaseUrl ?? "/api/tinkertoy",
  moduleLoader: createModuleLoader({
    cdnBaseUrl,
    shellVersion: config?.developerTools?.shellVersion ?? SHELL_VERSION,
    bundleOverrides: flattenBundleUrls(config?.devOverrides?.modules),
  }),
  viewLoader: createViewLoader({
    cdnBaseUrl,
    shellVersion: config?.developerTools?.shellVersion ?? SHELL_VERSION,
    bundleOverrides: flattenBundleUrls(config?.devOverrides?.views),
  }),
  theme: config?.theme ?? PURPLE_THEME,
  developerTools: config?.developerTools,
  kindredConfig: config?.kindredConfig,
});
